import {
  Button,
  Field,
  InfoLabel,
  makeStyles,
  mergeClasses,
  Radio,
  RadioGroup,
  Spinner,
} from "@fluentui/react-components";
import { useContext, useEffect, useState } from "react";
import ErrorMessage from "../../../components-v2/common/error/ErrorMessage";
import { NoDataCard } from "../../../components-v2/common/no-data-card/NoDataCard";
import RollingFilter from "../../../components-v2/filters/global-filter/filter-options/RollingFilter";
import { NotificationContext } from "../../../context/NotificationContext";
import { $sky60 } from "../../../cssjs/colors";
import { DateTimeSettingsEnum } from "../../../enum/app-settings/AppSettingsEnum";
import { useApiResource } from "../../../hooks/useApiResource";
import {
  IApiResourceResponse,
  IDateConfigValues,
} from "../../../models/app-settings/AppSettingsModels";
import {
  extractTimeRange,
  getDateConfigPayload,
} from "../../../utils/app-settings/AppSettingsHelper";
import { scrollToTop } from "../../../utils/common/CommonHelperFunctions";
import {
  AIRPORT_CODE_PARAMETER,
  EMPTY_STATE_MESSAGE,
  MESSAGEBAR_TYPE,
} from "../../../utils/common/Constants";
import { validateRollingTime } from "../../../utils/common/DateHelperFunctions";

interface IDateTimeSettingsProps {
  airportCode: string;
}

const dateTimeSettingsURL = process.env.REACT_APP_API_DATE_TIME_CONFIG;
const baseURL = process.env.REACT_APP_API_BASE_URL;
const dateConfigUrl = `${baseURL}${dateTimeSettingsURL}`;

const DEFAULT_APPLIED_VALUES: IDateConfigValues = {
  fromRollingHours: 0,
  fromRollingMinute: 0,
  toRollingHours: 0,
  toRollingMinute: 0,
  fromDurationHours: 0,
  fromDurationMinute: 0,
  toDurationHours: 23,
  toDurationMinute: 59,
  selectedConfig: DateTimeSettingsEnum.RollingHrs,
};

const rollingHrsInfo = "Set default rolling time based on current time.";
const timeDurationInfo =
  "Set default time duration in absolute time format for custom date time filter.";

const DateTimeSettings: React.FC<IDateTimeSettingsProps> = ({
  airportCode,
}) => {
  const classes = useStyles();
  const url = dateConfigUrl.replace(AIRPORT_CODE_PARAMETER, airportCode);
  const { addNotification } = useContext(NotificationContext);

  const [selectedOption, setSelectedOption] = useState<string>(
    DateTimeSettingsEnum.RollingHrs
  );

  const [fromRollingHours, setFromRollingHours] = useState<number>(0);
  const [fromRollingMinute, setFromRollingMinute] = useState<number>(0);
  const [toRollingHours, setToRollingHours] = useState<number>(0);
  const [toRollingMinute, setToRollingMinute] = useState<number>(0);

  const [fromDurationHours, setFromDurationHours] = useState<number>(0);
  const [fromDurationMinute, setFromDurationMinute] = useState<number>(0);
  const [toDurationHours, setToDurationHours] = useState<number>(23);
  const [toDurationMinute, setToDurationMinute] = useState<number>(59);

  const [appliedValues, setAppliedValues] = useState<IDateConfigValues>(
    DEFAULT_APPLIED_VALUES
  );

  const {
    data,
    isLoading,
    isError,
    update,
    isUpdating,
    isUpdateSuccess,
    isUpdateError,
  } = useApiResource<IApiResourceResponse>({
    queryKey: ["dateTimeKPISettings", airportCode],
    url: url,
    method: "PUT",
  });

  useEffect(() => {
    const datetimeConfigData = data?.timeSettings || [];
    const selectedConfig =
      data?.selectedConfig || DateTimeSettingsEnum.RollingHrs;

    if (datetimeConfigData?.length > 0) {
      const rollingConfig = datetimeConfigData.find(
        (item: any) => item.name === DateTimeSettingsEnum.RollingHrs
      );
      const durationConfig = datetimeConfigData.find(
        (item: any) => item.name === DateTimeSettingsEnum.TimeDuration
      );

      const rollingTime = extractTimeRange(rollingConfig);
      const durationTime = extractTimeRange(durationConfig);

      setFromRollingHours(rollingTime.fromHours);
      setFromRollingMinute(rollingTime.fromMinutes);
      setToRollingHours(rollingTime.toHours);
      setToRollingMinute(rollingTime.toMinutes);

      setFromDurationHours(durationTime.fromHours);
      setFromDurationMinute(durationTime.fromMinutes);
      setToDurationHours(durationTime.toHours);
      setToDurationMinute(durationTime.toMinutes);

      setAppliedValues((prevValues: IDateConfigValues) => ({
        ...prevValues,
        fromRollingHours: rollingTime.fromHours,
        fromRollingMinute: rollingTime.fromMinutes,
        toRollingHours: rollingTime.toHours,
        toRollingMinute: rollingTime.toMinutes,
        fromDurationHours: durationTime.fromHours,
        fromDurationMinute: durationTime.fromMinutes,
        toDurationHours: durationTime.toHours,
        toDurationMinute: durationTime.toMinutes,
      }));
    }

    setSelectedOption(selectedConfig);
    setAppliedValues((prevValues: IDateConfigValues) => ({
      ...prevValues,
      selectedConfig: selectedConfig,
    }));
  }, [data]);

  useEffect(() => {
    if (isUpdateSuccess) {
      addNotification(
        MESSAGEBAR_TYPE.WARNING,
        "Reload all open APOC tabs to apply changes"
      );
      addNotification(
        MESSAGEBAR_TYPE.SUCCESS,
        `Date Time settings settings saved successfully`
      );
      setAppliedValues({
        fromRollingHours,
        fromRollingMinute,
        toRollingHours,
        toRollingMinute,
        fromDurationHours,
        fromDurationMinute,
        toDurationHours,
        toDurationMinute,
        selectedConfig: selectedOption,
      });
    } else if (isUpdateError) {
      addNotification(
        MESSAGEBAR_TYPE.ERROR,
        "Failed to update Date Time settings"
      );
    }
  }, [isUpdateSuccess, isUpdateError]);

  const rollingTimeProps = {
    fromRollingtime: {
      hour: fromRollingHours,
      minute: fromRollingMinute,
      setHour: setFromRollingHours,
      setMinute: setFromRollingMinute,
    },
    toRollingtime: {
      hour: toRollingHours,
      minute: toRollingMinute,
      setHour: setToRollingHours,
      setMinute: setToRollingMinute,
    },
  };

  const durationTimeProps = {
    fromDurationtime: {
      hour: fromDurationHours,
      minute: fromDurationMinute,
      setHour: setFromDurationHours,
      setMinute: setFromDurationMinute,
    },
    toDurationtime: {
      hour: toDurationHours,
      minute: toDurationMinute,
      setHour: setToDurationHours,
      setMinute: setToDurationMinute,
    },
  };

  const rollingErrorMsg = validateRollingTime(
    fromRollingHours,
    fromRollingMinute,
    toRollingHours,
    toRollingMinute
  );

  const durationErrorMsg = validateRollingTime(
    fromDurationHours,
    fromDurationMinute,
    toDurationHours,
    toDurationMinute
  );

  const errorMsg = rollingErrorMsg || durationErrorMsg;

  const onSaveHandler = () => {
    if (isUpdating) {
      return;
    }

    const payload = getDateConfigPayload({
      fromRollingHours,
      fromRollingMinute,
      toRollingHours,
      toRollingMinute,
      fromDurationHours,
      fromDurationMinute,
      toDurationHours,
      toDurationMinute,
      selectedConfig: selectedOption,
    });
    update(payload);
    scrollToTop();
  };

  const onCancelHandler = () => {
    setFromRollingHours(appliedValues.fromRollingHours);
    setFromRollingMinute(appliedValues.fromRollingMinute);
    setToRollingHours(appliedValues.toRollingHours);
    setToRollingMinute(appliedValues.toRollingMinute);
    setFromDurationHours(appliedValues.fromDurationHours);
    setFromDurationMinute(appliedValues.fromDurationMinute);
    setToDurationHours(appliedValues.toDurationHours);
    setToDurationMinute(appliedValues.toDurationMinute);
    setSelectedOption(appliedValues.selectedConfig);
  };

  return (
    <article
      data-testid="date-time-settings"
      className={mergeClasses(classes.container, "amd-container-vspacebtwn")}
    >
      {isLoading && (
        <Spinner
          labelPosition="below"
          label="Loading..."
          className={classes.container}
        />
      )}
      {isError && (
        <div style={{ margin: "auto" }}>
          <NoDataCard message={EMPTY_STATE_MESSAGE.Data} />
        </div>
      )}
      {!isError && !isLoading && (
        <>
          <Field className="pa-sm-2 fw-500">
            <RadioGroup
              value={selectedOption}
              onChange={(_ev, data) => setSelectedOption(data.value)}
              layout="horizontal"
              className={classes.radioGroup}
            >
              <div>
                <Radio
                  value={DateTimeSettingsEnum.RollingHrs}
                  label={
                    <InfoLabel info={rollingHrsInfo}>Rolling hrs</InfoLabel>
                  }
                  className="pb-1"
                />
                <RollingFilter
                  fromRollingtime={rollingTimeProps.fromRollingtime}
                  toRollingtime={rollingTimeProps.toRollingtime}
                  className={classes.radioButtonContainer}
                  redirectionText={false}
                  type="dateTime"
                  padHour
                  padMinute
                />
                {rollingErrorMsg.length > 0 && (
                  <ErrorMessage
                    errorMsg={errorMsg}
                    className={classes.errorMsg}
                  />
                )}
              </div>
              <div>
                <Radio
                  value={DateTimeSettingsEnum.TimeDuration}
                  label={
                    <InfoLabel info={timeDurationInfo}>Time duration</InfoLabel>
                  }
                  className="pb-1"
                />
                <RollingFilter
                  fromRollingtime={durationTimeProps.fromDurationtime}
                  toRollingtime={durationTimeProps.toDurationtime}
                  className={classes.radioButtonContainer}
                  allowNegative={false}
                  label="Time"
                  redirectionText={false}
                  type="dateTime"
                  padHour
                  padMinute
                />
                {durationErrorMsg.length > 0 && (
                  <ErrorMessage
                    errorMsg={errorMsg}
                    className={classes.errorMsg}
                  />
                )}
              </div>
            </RadioGroup>
          </Field>

          <footer className="d-flex pl-2 pb-2 gap-10">
            <Button onClick={onCancelHandler}>Cancel</Button>
            <Button
              appearance="primary"
              disabled={errorMsg.length > 0}
              className={classes.button}
              onClick={onSaveHandler}
            >
              {isUpdating ? "Saving..." : "Save"}
            </Button>
          </footer>
        </>
      )}
    </article>
  );
};

export default DateTimeSettings;

const useStyles = makeStyles({
  container: {
    minHeight: "422px",
  },
  radioGroup: {
    gap: "30px",
  },
  radioButtonContainer: {
    marginLeft: "26px",
    border: "2px solid #00000042",
    borderRadius: "8px",
    padding: "18px 20px",
  },
  button: {
    "&:disabled": {
      border: `1px solid ${$sky60} !important`,
    },
  },
  errorMsg: {
    display: "flex",
    alignItems: "center",
  },
});
